import React, { useState, useEffect } from 'react';
import Menu from '../menu/Menu';
import SelectPGAllocated from './SelectPGAllocated';
import SelectBed from './SelectBed';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import '../common/rtCommon.css';
import './AllocateBed.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaEdit, FaTimes } from 'react-icons/fa';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastError, toastSuccess } from '../common/toast';

const MoveBed = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State management
  const [selectedPG, setSelectedPG] = useState(null);
  const [currentAllocation, setCurrentAllocation] = useState(null);
  const [selectedBed, setSelectedBed] = useState(null);
  const [newEndDate, setNewEndDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [payableAmount, setPayableAmount] = useState(0);
  
  // UI State
  const [showSelectPG, setShowSelectPG] = useState(true);
  const [showSelectBed, setShowSelectBed] = useState(false);
  const [loadingAllocation, setLoadingAllocation] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Discount states for new allocation
  const [hasDiscount, setHasDiscount] = useState(false);
  const [discountType, setDiscountType] = useState('amount');
  const [discountValue, setDiscountValue] = useState('');
  const [discountedRent, setDiscountedRent] = useState(0);

  // Calculate discounted rent for new allocation
  useEffect(() => {
    if (selectedBed && hasDiscount && discountValue) {
      const bedRent = parseFloat(selectedBed.bed_rent) || 0;
      let discount = 0;
      
      if (discountType === 'amount') {
        discount = parseFloat(discountValue) || 0;
        discount = Math.min(discount, bedRent);
      } else if (discountType === 'percentage') {
        const percentage = parseFloat(discountValue) || 0;
        const validPercentage = Math.min(Math.max(percentage, 0), 100);
        discount = (bedRent * validPercentage) / 100;
      }
      
      const finalRent = bedRent - discount;
      setDiscountedRent(finalRent);
      setPayableAmount(finalRent);
    } else if (selectedBed) {
      const bedRent = parseFloat(selectedBed.bed_rent) || 0;
      setDiscountedRent(bedRent);
      setPayableAmount(bedRent);
    }
  }, [selectedBed, hasDiscount, discountType, discountValue]);

  // Fetch allocation details when PG is selected
  useEffect(() => {
    if (selectedPG) {
      fetchAllocationDetails();
    }
  }, [selectedPG]);

  // Set start date when current allocation end date is set
  useEffect(() => {
    if (newEndDate) {
      const nextDay = new Date(newEndDate);
      nextDay.setDate(nextDay.getDate() + 1);
      setStartDate(nextDay);
    }
  }, [newEndDate]);

  const fetchAllocationDetails = async () => {
    setLoadingAllocation(true);
    try {
      const response = await apiGet(`/allocation/by-pg/${selectedPG.pg_id}`);
      setCurrentAllocation(response.data);
      
      // Set default new end date to today
      const today = new Date();
      setNewEndDate(today);
    } catch (error) {
      console.error('Error fetching allocation details:', error);
      toastError('Failed to load current allocation details');
      setCurrentAllocation(null);
    } finally {
      setLoadingAllocation(false);
    }
  };

  const validateInputs = () => {
    const newErrors = {};
    
    if (!selectedPG) {
      newErrors.selectedPG = 'Paying Guest must be selected.';
    }
    if (!currentAllocation) {
      newErrors.currentAllocation = 'Current allocation not found.';
    }
    if (!newEndDate) {
      newErrors.newEndDate = 'End date for current allocation is required.';
    }
    if (!selectedBed) {
      newErrors.selectedBed = 'New bed must be selected.';
    }
    if (!startDate) {
      newErrors.startDate = 'Start date for new allocation is required.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSelectPG = (pg) => {
    setSelectedPG(pg);
    setShowSelectPG(false);
  };

  const handleSelectBed = (bed) => {
    setSelectedBed(bed);
    const bedRent = parseFloat(bed.bed_rent) || 0;
    setPayableAmount(bedRent);
    setShowSelectBed(false);
    // Reset discount when new bed is selected
    setHasDiscount(false);
    setDiscountValue('');
  };

  const handleMoveBed = async () => {
    if (validateInputs()) {
      setIsMoving(true);
      try {
        const moveData = {
          current_allocation_id: currentAllocation.allocation_id,
          current_end_date: newEndDate.toISOString().split('T')[0],
          new_start_date: startDate.toISOString().split('T')[0],
          new_end_date: endDate ? endDate.toISOString().split('T')[0] : null,
          new_bed_id: selectedBed.bed_id,
          new_room_id: selectedBed.room_id,
          discount_type: hasDiscount ? discountType : null,
          discount_value: hasDiscount ? parseFloat(discountValue) || 0 : null,
          total_amount: parseFloat(selectedBed.bed_rent) || 0,
          discounted_rent: hasDiscount ? discountedRent : null,
          payable_amount: payableAmount,
        };

        const response = await apiPost('/allocation/change-allocation', moveData);
        
        if (response.status === 200 || response.status === 201) {
          toastSuccess('Bed moved successfully!');
          // Navigate to new allocation details page
          const newAllocationId = response.data?.new_allocation_id;
          if (newAllocationId) {
            navigate(`/bedallocation/allocation/${newAllocationId}`);
          }
        }
      } catch (error) {
        console.error('Move failed:', error);
        if (error.response?.data?.detail) {
          toastError('Error moving bed: ' + error.response.data.detail);
        } else {
          toastError('Failed to move bed. Please try again.');
        }
      } finally {
        setIsMoving(false);
      }
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleClearPG = () => {
    setSelectedPG(null);
    setCurrentAllocation(null);
    setShowSelectPG(true);
    setErrors({ ...errors, selectedPG: undefined });
  };

  const handleEditPG = () => {
    setSelectedPG(null);
    setCurrentAllocation(null);
    setShowSelectPG(true);
  };

  // Calculate minimum end date for current allocation (today)
  const getMinEndDate = () => {
    return new Date();
  };

  // Calculate minimum start date for new allocation (next day after current end date)
  const getMinStartDate = () => {
    if (!newEndDate) return new Date();
    const nextDay = new Date(newEndDate);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay;
  };

  // Calculate rent difference
  const getRentDifference = () => {
    if (!currentAllocation || !selectedBed) return 0;
    const currentRent = parseFloat(currentAllocation.payable_amount) || 0;
    const newRent = payableAmount || 0;
    return newRent - currentRent;
  };

  return (
    <div>
      {showSelectPG ? (
        <div className="rt-layout">
          <Menu className="rt-menu" activePath={location.pathname} />
          <div className="rt-main-content">
            <div className="rt-title-row">
              <h2 className="rt-title">Move Bed</h2>
              <div className="rt-actions">
                <RTButton variant="secondary" onClick={handleCancel}>
                  &#8592; Back
                </RTButton>
              </div>
            </div>
            <div className="rt-main-section">
              <SelectPGAllocated onSelect={handleSelectPG} />
            </div>
          </div>
        </div>
      ) : showSelectBed ? (
        <SelectBed onSelect={handleSelectBed} />
      ) : (
        <div className="rt-layout">
          <Menu className="rt-menu" activePath={location.pathname} />
          <div className="rt-main-content">
            <div className="rt-title-row">
              <h2 className="rt-title">Move Bed</h2>
              <div className="rt-actions">
                <RTButton variant="secondary" onClick={handleCancel}>
                  &#8592; Back
                </RTButton>
              </div>
            </div>
            <div className="allocate-bed-section">
              {/* Left Section */}
              <div className="allocate-bed-form">
                {/* Current Allocation Section */}
                <div className="rt-form-group">
                  <h2 className="rt-form-title">Current Allocation</h2>
                  
                  {/* Paying Guest Selection */}
                  <div className="rt-form-group">
                    <label>
                      Paying Guest <span style={{ color: 'red' }}>*</span>
                    </label>
                    <div className="pg-selection-container">
                      {!selectedPG ? (
                        <div 
                          className="pg-selection-empty clickable"
                          onClick={() => setShowSelectPG(true)}
                        >
                          <span style={{ fontWeight: 'bold'}}>Select Paying Guest</span>
                        </div>
                      ) : (
                        <div className="pg-selection-filled">
                          <span className="selected-pg-name">{selectedPG.name}</span>
                          <div className="pg-actions">
                            <button 
                              className="icon-button edit-button" 
                              onClick={handleEditPG}
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button 
                              className="icon-button clear-button" 
                              onClick={handleClearPG}
                              title="Clear"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    {errors.selectedPG && (
                      <div className="rt-form-error">{errors.selectedPG}</div>
                    )}
                  </div>

                  {/* Current Allocation Details */}
                  {loadingAllocation ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <RTSpinner size={50} message="Loading current allocation..." />
                    </div>
                  ) : currentAllocation ? (
                    <>
                      <div className="rt-form-group">
                        <label>Current Building & Room</label>
                        <div className="rt-input" style={{ backgroundColor: '#f5f5f5' }}>
                          {currentAllocation.building_name} - {currentAllocation.room_type}
                        </div>
                      </div>
                      
                      <div className="rt-form-group">
                        <label>Current Start Date</label>
                        <div className="rt-input" style={{ backgroundColor: '#f5f5f5' }}>
                          {formatDateToDDMMYYYY(currentAllocation.start_date)}
                        </div>
                      </div>

                      <div className="rt-form-group">
                        <label>
                          End Date for Current Allocation <span style={{ color: 'red' }}>*</span>
                        </label>
                        <DatePicker
                          selected={newEndDate}
                          onChange={(date) => setNewEndDate(date)}
                          dateFormat="dd/MM/yyyy"
                          className="custom-datepicker rt-input"
                          minDate={getMinEndDate()}
                          placeholderText="DD/MM/YYYY"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          autoComplete="off"
                        />
                        {errors.newEndDate && (
                          <div className="rt-form-error">{errors.newEndDate}</div>
                        )}
                      </div>
                    </>
                  ) : selectedPG && (
                    <div className="rt-form-error">No active allocation found for this PG</div>
                  )}
                </div>

                {/* Future Allocation Section */}
                {currentAllocation && (
                  <div className="rt-form-group" style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                    <h2 className="rt-form-title">New Allocation</h2>
                    
                    <div className="rt-form-group">
                      <label>
                        Start Date <span style={{ color: 'red' }}>*</span>
                      </label>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        dateFormat="dd/MM/yyyy"
                        className="custom-datepicker rt-input"
                        minDate={getMinStartDate()}
                        placeholderText="DD/MM/YYYY"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        autoComplete="off"
                      />
                      {errors.startDate && (
                        <div className="rt-form-error">{errors.startDate}</div>
                      )}
                    </div>
                    
                    <div className="rt-form-group">
                      <label>End Date</label>
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        dateFormat="dd/MM/yyyy"
                        className="custom-datepicker rt-input"
                        minDate={startDate}
                        placeholderText="DD/MM/YYYY"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        autoComplete="off"
                      />
                    </div>

                    <div className="rt-form-group">
                      <label>
                        New Room and Bed <span style={{ color: 'red' }}>*</span>
                      </label>
                      <div className="bed-selection-container">
                        {!selectedBed ? (
                          <div 
                            className="bed-selection-empty clickable"
                            onClick={() => setShowSelectBed(true)}
                          >
                            <span style={{ fontWeight: 'bold'}}>Select New Room and Bed</span>
                          </div>
                        ) : (
                          <div className="bed-selection-filled">
                            <span className="selected-bed-name">
                              {selectedBed.building_name} : {selectedBed.room_name}
                            </span>
                            <div className="bed-actions">
                              <button 
                                className="icon-button edit-button" 
                                onClick={() => {
                                  setSelectedBed(null);
                                  setPayableAmount(0);
                                  setShowSelectBed(true);
                                }}
                                title="Edit"
                              >
                                <FaEdit />
                              </button>
                              <button 
                                className="icon-button clear-button" 
                                onClick={() => {
                                  setSelectedBed(null);
                                  setPayableAmount(0);
                                  setErrors({ ...errors, selectedBed: undefined });
                                }}
                                title="Clear"
                              >
                                <FaTimes />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      {errors.selectedBed && (
                        <div className="rt-form-error">{errors.selectedBed}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Section - Summary */}
              <div className="allocate-bed-summary">
                <h2 className="summary-title">Move Summary</h2>
                
                {/* Current Allocation Summary */}
                {currentAllocation && (
                  <div className="summary-section">
                    <h3 className="summary-subtitle">Previous Allocation</h3>
                    <div className="summary-item">
                      <span className="summary-label">Building:</span>
                      <span className="summary-value">{currentAllocation.building_name}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Room Type:</span>
                      <span className="summary-value">{currentAllocation.room_type}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Current Rent:</span>
                      <span className="summary-value">₹{currentAllocation.payable_amount?.toLocaleString()}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Ending On:</span>
                      <span className="summary-value">
                        {formatDateToDDMMYYYY(newEndDate)}
                      </span>
                    </div>
                  </div>
                )}

                {/* New Allocation Summary */}
                <div className="summary-section">
                  <h3 className="summary-subtitle">New Allocation Details</h3>
                  <div className="summary-item">
                    <span className="summary-label">Start Date:</span>
                    <span className="summary-value">
                      {formatDateToDDMMYYYY(startDate)}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">End Date:</span>
                    <span className="summary-value">
                      {formatDateToDDMMYYYY(endDate)}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">New Building:</span>
                    <span className="summary-value">
                      {selectedBed ? selectedBed.building_name : '-'}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">New Room:</span>
                    <span className="summary-value">
                      {selectedBed ? selectedBed.room_name : '-'}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">New Bed Rent:</span>
                    <span className="summary-value">
                      {selectedBed ? `₹${selectedBed.bed_rent}` : '-'}
                    </span>
                  </div>
                  
                  {/* Discount Section */}
                  {selectedBed && (
                    <>
                      <div className="summary-item discount-checkbox">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={hasDiscount}
                            onChange={(e) => {
                              setHasDiscount(e.target.checked);
                              if (!e.target.checked) {
                                setDiscountValue('');
                                setDiscountType('amount');
                              }
                            }}
                          />
                          <span className="summary-label">Apply Discount</span>
                        </label>
                      </div>
                      
                      {hasDiscount && (
                        <>
                          <div className="discount-type-section">
                            <label className="radio-label">
                              <input
                                type="radio"
                                value="amount"
                                checked={discountType === 'amount'}
                                onChange={(e) => {
                                  setDiscountType(e.target.value);
                                  setDiscountValue('');
                                }}
                              />
                              <span className="summary-label">Amount</span>
                            </label>
                            <label className="radio-label">
                              <input
                                type="radio"
                                value="percentage"
                                checked={discountType === 'percentage'}
                                onChange={(e) => {
                                  setDiscountType(e.target.value);
                                  setDiscountValue('');
                                }}
                              />
                              <span className="summary-label">Percentage</span>
                            </label>
                          </div>
                          
                          <div className="summary-item">
                            <span className="summary-label">
                              Discount {discountType === 'percentage' ? '(%)' : '(₹)'}:
                            </span>
                            <input
                              type="number"
                              className="discount-input"
                              value={discountValue}
                              onChange={(e) => setDiscountValue(e.target.value)}
                              placeholder={discountType === 'percentage' ? '0-100' : '0'}
                              min="0"
                              max={discountType === 'percentage' ? '100' : selectedBed.bed_rent}
                            />
                          </div>
                          
                          <div className="summary-item">
                            <span className="summary-label">Discounted Rent:</span>
                            <span className="summary-value">
                              ₹{discountedRent.toFixed(2)}
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Rent Comparison */}
                {currentAllocation && selectedBed && (
                  <div className="summary-section">
                    <h3 className="summary-subtitle">Rent Comparison</h3>
                    <div className="summary-item">
                      <span className="summary-label">Previous Rent:</span>
                      <span className="summary-value">₹{currentAllocation.payable_amount?.toLocaleString()}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">New Rent:</span>
                      <span className="summary-value">₹{payableAmount.toFixed(2)}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Difference:</span>
                      <span className={`summary-value ${getRentDifference() >= 0 ? 'text-success' : 'text-danger'}`} 
                            style={{ 
                              fontWeight: 'bold',
                              color: getRentDifference() >= 0 ? '#28a745' : '#dc3545'
                            }}>
                        {getRentDifference() >= 0 ? '+' : ''}₹{getRentDifference().toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="summary-actions" style={{ marginTop: '20px' }}>
                  <RTButton
                    variant="secondary"
                    onClick={handleCancel}
                    style={{ marginRight: '10px' }}
                  >
                    Cancel
                  </RTButton>
                  <RTButton
                    variant="primary"
                    disabled={!selectedPG || !currentAllocation || !newEndDate || !selectedBed || !startDate || isMoving}
                    onClick={handleMoveBed}
                  >
                    {isMoving ? (
                      <>
                        <RTSpinner size="small" showMessage={false} style={{ marginRight: '8px' }} />
                        Moving...
                      </>
                    ) : (
                      'Move Bed'
                    )}
                  </RTButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MoveBed;